import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { useNavigate } from 'react-router-dom';
import { useNotifications, useUnreadCount, useMarkRead, useMarkAllRead } from '../hooks/useNotifications';

const typeIcons: Record<string, React.ReactElement> = {
  job_failed: <ErrorIcon color="error" fontSize="small" />,
  optimization_failed: <ErrorIcon color="error" fontSize="small" />,
  autopilot_failed: <WarningIcon color="warning" fontSize="small" />,
  optimization_completed: <CheckCircleIcon color="success" fontSize="small" />,
  autopilot_completed: <CheckCircleIcon color="success" fontSize="small" />,
  quota_warning_80: <WarningIcon color="warning" fontSize="small" />,
  quota_exhausted_100: <ErrorIcon color="error" fontSize="small" />,
};

function relativeTime(dateStr: string): string {
  const utc = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
  const diff = Date.now() - new Date(utc).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min.`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `vor ${hrs} Std.`;
  const days = Math.floor(hrs / 24);
  return `vor ${days} Tag${days > 1 ? 'en' : ''}`;
}

const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { data: notifications } = useNotifications();
  const { data: unreadCount } = useUnreadCount();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const handleClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleItemClick = (notif: { id: number; link: string | null; is_read: boolean }) => {
    if (!notif.is_read) markRead.mutate(notif.id);
    if (notif.link) navigate(notif.link);
    handleClose();
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleClick} sx={{ color: 'text.secondary' }}>
        <Badge badgeContent={unreadCount || 0} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, maxHeight: 480 } } }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={600}>Benachrichtigungen</Typography>
          {(unreadCount || 0) > 0 && (
            <Button size="small" onClick={() => markAllRead.mutate()}>
              Alle als gelesen markieren
            </Button>
          )}
        </Box>
        <Divider />
        {notifications && notifications.length > 0 ? (
          <List dense sx={{ p: 0 }}>
            {notifications.map((n) => (
              <ListItemButton
                key={n.id}
                onClick={() => handleItemClick(n)}
                sx={{ bgcolor: n.is_read ? 'transparent' : 'action.hover' }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {typeIcons[n.type] || <NotificationsIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary={n.title}
                  secondary={`${n.message} · ${relativeTime(n.created_at)}`}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: n.is_read ? 400 : 600 }}
                  secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">Keine Benachrichtigungen</Typography>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
